import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';

import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { editorCommandToPMCommand } from '@atlaskit/editor-common/preset/editor-commands';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { showLinkToolbar } from '@atlaskit/editor-plugin-hyperlink/commands';

import { AppIcon } from '@atlassian/search-ai/app-icon';

import { getSetPreferredTabKey } from '../components/RovoLinkPickerTabPreferenceContext.tsx';

const i18n = defineMessages({
	appLinkDescription: {
		id: 'editor-features.third-party-quick-insert.app-link.description',
		defaultMessage: 'Insert a link from {appName}',
		description:
			'Description for a quick-insert (slash command) menu item that inserts a link from a third-party app. {appName} is the product name (e.g. Google Drive, Figma) and must not be translated.',
	},
	embedTitle: {
		id: 'editor-features.third-party-quick-insert.embed.title',
		defaultMessage: 'Embed',
		description:
			'Title for a quick-insert (slash command) menu item that opens the insert/link toolbar to embed external content.',
	},
	embedDescription: {
		id: 'editor-features.third-party-quick-insert.embed.description',
		defaultMessage: 'Insert a link or embed external content',
		description:
			'Description for a quick-insert (slash command) menu item that opens the insert/link toolbar to embed external content.',
	},
});

interface ThirdPartyAppConfig {
	tabKey: string;
	/** Product/brand name — intentionally not translated. */
	title: string;
	keywords: string[];
	appIconKey: string;
	categories?: string[];
}

const THIRD_PARTY_APPS: ThirdPartyAppConfig[] = [
	{
		tabKey: 'google-drive',
		title: 'Google Drive',
		keywords: ['google', 'drive', 'gdrive', 'google drive', 'link', 'embed'],
		appIconKey: 'Google Drive',
		categories: ['external-content'],
	},
	{
		tabKey: 'figma',
		title: 'Figma',
		keywords: ['figma', 'design', 'link', 'embed'],
		appIconKey: 'Figma',
		categories: ['external-content'],
	},
	{
		tabKey: 'amplitude',
		title: 'Amplitude',
		keywords: ['amplitude', 'analytics', 'link', 'embed'],
		appIconKey: 'amplitude',
		categories: ['external-content'],
	},
	{
		tabKey: 'loom',
		title: 'Loom',
		keywords: ['loom', 'video', 'recording', 'link', 'embed'],
		appIconKey: 'Loom',
		categories: ['external-content'],
	},
	{
		tabKey: 'sharepoint',
		title: 'SharePoint',
		keywords: ['sharepoint', 'microsoft', 'link', 'embed'],
		appIconKey: 'sharepoint',
		categories: ['external-content'],
	},
];

interface CreateThirdPartyQuickInsertExtensionProviderOptions {
	editorActions: EditorActions;
	intlNext: IntlShape;
	cohort: string;
}

export const createThirdPartyQuickInsertExtensionProvider = ({
	editorActions,
	intlNext,
	cohort,
}: CreateThirdPartyQuickInsertExtensionProviderOptions): DefaultExtensionProvider<Parameters> => {
	const quickInsertModules: ExtensionModule[] = THIRD_PARTY_APPS.map((app) => ({
		key: `third-party-embed-${app.tabKey}`,
		title: app.title,
		description: intlNext.formatMessage(i18n.appLinkDescription, { appName: app.title }),
		keywords: app.keywords,
		categories: ['external-content'],
		icon: async () => () => <AppIcon app={app.appIconKey} size="small" />,
		action: async () => {
			// Set the preferred tab key so the Rovo Link Picker opens on the correct tab
			getSetPreferredTabKey()(app.tabKey);

			// Defer the link toolbar open to let the type-ahead settle
			requestAnimationFrame(() => {
				const view = editorActions._privateGetEditorView();
				if (!view) {
					// The editor view went away before we could open the toolbar (e.g. the
					// editor unmounted between selection and this frame). The preference was
					// set synchronously above, but RovoLinkPickerAdapter will never mount to
					// clear it — so clear it here to avoid leaking a stale tab into the next
					// link picker open.
					getSetPreferredTabKey()(undefined);
					return;
				}
				editorCommandToPMCommand(showLinkToolbar(INPUT_METHOD.QUICK_INSERT, undefined))(
					view.state,
					view.dispatch,
				);
			});

			// Don't insert any ADF node
			return undefined;
		},
	}));

	const embedModule: ExtensionModule = {
		key: 'embed',
		title: intlNext.formatMessage(i18n.embedTitle),
		description: intlNext.formatMessage(i18n.embedDescription),
		keywords: ['embed', 'link', 'url'],
		categories: ['external-content'],
		// Lower priority sorts earlier in the slash menu. Only boost the `/embed`
		// item for the `prioritizeEmbedInQIM` cohort; otherwise leave it unset so it
		// sorts by default relevance.
		priority: cohort === 'prioritizeEmbedInQIM' ? -300 : undefined,
		icon: async () => () => <AppIcon app={null} size="small" />,
		action: async () => {
			// Defer the link toolbar open to let the type-ahead settle
			requestAnimationFrame(() => {
				const view = editorActions._privateGetEditorView();
				if (!view) {
					return;
				}
				editorCommandToPMCommand(showLinkToolbar(INPUT_METHOD.QUICK_INSERT, undefined))(
					view.state,
					view.dispatch,
				);
			});

			// Don't insert any ADF node
			return undefined;
		},
	};

	const manifest: ExtensionManifest = {
		title: 'Third-party embeds',
		description: 'Quick-insert items for third-party app links',
		type: 'com.atlassian.confluence.macro.core',
		key: 'third-party-quick-insert-embeds',
		icons: {
			'48': async () => () => <AppIcon app={null} size="small" />,
		},
		modules: {
			quickInsert: [embedModule, ...quickInsertModules],
		},
	};

	return new DefaultExtensionProvider([manifest]);
};
